import { useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { MonthFilterRow } from '../components/MonthFilterRow';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { DayBars, StackBar } from '../components/Charts';
import {
  dayLabel, daysOfMonth, monthDay, monthLabel, monthName,
  monthRange, money, weekdayDay,
} from '../lib/scope';
import { useReportMonth } from '../lib/useReportMonth';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { useAppointments, useInvoices } from '../lib/useManagerData';
import { ManagerSurface } from '../components/ManagerSurface';
import { BranchBars } from '../components/BranchBars';
import { ManagerMenu } from '../components/ManagerMenu';

const METHODS: Array<{ key: string; label: string; colour: string }> = [
  { key: 'cash', label: 'Cash', colour: 'var(--brand-500)' },
  { key: 'card', label: 'Card', colour: 'var(--brand-300)' },
  { key: 'mobile_payment', label: 'InstaPay', colour: 'var(--info-600)' },
  { key: 'insurance', label: 'Insurance', colour: 'var(--beige-500)' },
  { key: 'bank_transfer', label: 'Transfer', colour: 'var(--ink-400)' },
];

/**
 * Clinic manager · The month in charts — `g_charts.dc.html`.
 *
 * THE MONTH AS A SHAPE, NOT A TABLE. The board says it in its closing line, and
 * says how: *CSS bars, no charting library*. Nothing on a page of charts is a
 * detail of something else, so there is NO PANE.
 *
 * CLICKING A DAY OPENS THAT DAY'S LEDGER — the board's own caption promises it
 * ("click a bar for that day's ledger"), which makes the bars controls rather
 * than decoration.
 */
export function ManagerChartsPage() {
  const go = useNavigate();
  /* The day's ledger is the manager's page; the accountant reads this same chart
     but has no ledger to open, so for them a bar is a figure, not a link. */
  const { surface } = useShellIdentity();
  const canOpenLedger = surface === 'clinic-manager';
  const { month, setMonth, compare, setCompare } = useReportMonth();
  const [query, setQuery] = useState('');

  const range = useMemo(() => monthRange(month), [month]);
  /* THE COMPARISON MONTH IS READ, not just labelled. `vs Jul ▾` sat on this
     screen changing nothing at all — a control that does not do what its label
     says (§8) — and the VISIT MIX card underneath it printed a bare count where
     the board prints an observation ("follow-up share ▲ 3rd month straight").
     One read of the comparison month answers both. */
  const prevRange = useMemo(() => monthRange(compare), [compare]);
  const invoices = useInvoices(range);
  const appointments = useAppointments(range);
  const appointmentsBefore = useAppointments(prevRange);

  usePageMeta({ title: 'The month in charts' });
  /* A month still being read is not an empty month. */
  const counting = invoices.isLoading || appointments.isLoading || appointmentsBefore.isLoading;

  const collected = invoices.page.rows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);

  const perDay = useMemo(() => {
    const sums = new Map<string, number>();
    for (const inv of invoices.page.rows) {
      sums.set(inv.date, (sums.get(inv.date) ?? 0) + Number(inv.amountPaid || 0));
    }
    return daysOfMonth(month).map((d) => ({
      key: d, value: sums.get(d) ?? 0, label: `${dayLabel(d)} — ${money(sums.get(d) ?? 0)}`,
    }));
  }, [invoices.page.rows, month]);

  const visits = appointments.page.rows.filter(
    (a) => a.status !== 'cancelled' && a.status !== 'no-show');

  const hours = useMemo(() => {
    const counts = new Map<number, number>();
    for (const a of visits) counts.set(Number((a.time || '00').slice(0, 2)),
      (counts.get(Number((a.time || '00').slice(0, 2))) ?? 0) + 1);
    const present = [...counts.keys()].sort((a, b) => a - b);
    const from = present.length ? present[0] : 9;
    const to = present.length ? present[present.length - 1] : 18;
    return Array.from({ length: Math.max(1, to - from + 1) }, (_, i) => {
      const h = from + i;
      return { key: String(h), value: counts.get(h) ?? 0, label: `${hour12(h)} — ${counts.get(h) ?? 0} visits` };
    });
  }, [visits]);
  const peakHour = hours.reduce<{ key: string; value: number } | null>(
    (p, h) => (!p || h.value > p.value ? h : p), null);

  const methodSlices = METHODS.map((m) => ({
    key: m.key, label: m.label, colour: m.colour,
    value: invoices.page.rows.filter((i) => i.paymentMethod === m.key)
      .reduce((s, i) => s + Number(i.amountPaid || 0), 0),
  }));

  const followUps = visits.filter((a) => a.appointmentType === 'follow-up').length;
  const visitSlices = [
    { key: 'new', label: 'New', value: visits.length - followUps, colour: 'var(--success-600)' },
    { key: 'fu', label: 'Follow-up', value: followUps, colour: 'var(--brand-300)' },
  ];

  /* The follow-up share, this month against the comparison month — the board's
     own observation, made out of the two months the filter row names rather
     than the three-month series nothing keeps. */
  const fuShare = visits.length ? Math.round((followUps / visits.length) * 100) : null;
  const fuShareBefore = followUpShare(appointmentsBefore.page.rows);
  const visitMixSub =
    counting ? 'counting the month…'
    : fuShare === null
      ? `no visits in ${monthLabel(month).split(' ')[0]}`
      : fuShareBefore === null
        ? `follow-up share ${fuShare}% · nothing in ${monthName(compare)} to compare it with`
        : fuShare > fuShareBefore
          ? `follow-up share ▲ ${fuShare - fuShareBefore} pts vs ${monthName(compare)}`
          : fuShare < fuShareBefore
            ? `follow-up share ▼ ${fuShareBefore - fuShare} pts vs ${monthName(compare)}`
            : `follow-up share level with ${monthName(compare)}`;

  const peakDay = perDay.reduce<{ key: string; value: number } | null>(
    (p, b) => (!p || b.value > p.value ? b : p), null);

  const crumb = usePhoneCrumb('Reports', `${monthName(month)} · charts`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'The month in charts',
      sub: `${monthLabel(month)} · all branches`,
      search: 'Search a day, an hour, a mix…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(`month-in-charts-${month}`, ['Day', 'Collected'],
            perDay.map((d) => [d.key, Math.round(d.value)])),
      },
      /* NO PANE — nothing on a page of charts is a detail of something else. */
    },
    [month, compare, query, collected, visits.length],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <MonthFilterRow
          month={month} onMonth={setMonth}
          compare={compare} onCompare={setCompare}
          branchLine="all branches"
        />

        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1.4, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">Collected per day</p>
              <DayBars
                bars={perDay}
                height={110}
                left={monthDay(range.start)}
                middle={
                  peakDay && peakDay.value > 0
                    /* The board names the WEEKDAY of the peak — "peak:
                       Thursday 28" — because which DAY of the week the clinic
                       is busiest is the thing a manager staffs around. */
                    ? `peak: ${weekdayDay(peakDay.key)}${darkClause(perDay)}${canOpenLedger ? ' · click a bar for that day’s ledger' : ''}`
                    : 'nothing collected this month'
                }
                right={monthDay(range.end)}
                onPick={canOpenLedger ? (b) => go(`/clinic-manager/today/ledger?day=${b.key}`) : undefined}
              />
            </Card>
            <Card>
              <p className="t-ovl mut mb8">Busiest hours</p>
              <DayBars
                bars={hours}
                height={70}
                left={hours.length ? hour12(Number(hours[0].key)) : '—'}
                middle={counting ? 'counting…' : peakHour && peakHour.value > 0 ? `${hour12(Number(peakHour.key))} peak` : 'no visits yet'}
                right={hours.length ? hour12(Number(hours[hours.length - 1].key)) : '—'}
              />
            </Card>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">Payment method mix</p>
              <StackBar slices={methodSlices} />
            </Card>
            <Card>
              <p className="t-ovl mut mb8">Visit mix</p>
              <StackBar slices={visitSlices} percent sub={visitMixSub} />
            </Card>
            <BranchBars invoices={invoices.page.rows} />
          </div>
        </div>

      </div>
    </div>
  );
}

/** The share of a month's real visits that were follow-ups, or null if none. */
function followUpShare(rows: Array<{ status?: string; appointmentType?: string }>): number | null {
  const visits = rows.filter((a) => a.status !== 'cancelled' && a.status !== 'no-show');
  if (!visits.length) return null;
  return Math.round(
    (visits.filter((a) => a.appointmentType === 'follow-up').length / visits.length) * 100);
}

/**
 * "Fridays dark" — the board's clause, which names a RECURRING WEEKDAY rather
 * than counting dates, because a clinic that is shut on Fridays is a fact about
 * the week and four separate dark dates is not. When the dark days do not all
 * fall on one weekday there is no weekly rule to name, so they are counted.
 */
function darkClause(bars: Array<{ key: string; value: number }>): string {
  const dark = bars.filter((b) => b.value === 0);
  if (!dark.length) return '';
  const days = new Set(dark.map((b) => {
    const [y, m, d] = b.key.split('-').map(Number);
    return new Date(y, m - 1, d).getDay();
  }));
  if (days.size === 1 && dark.length > 1) {
    const [y, m, d] = dark[0].key.split('-').map(Number);
    const name = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
    return ` · ${name}s dark`;
  }
  return ` · ${dark.length} day${dark.length === 1 ? '' : 's'} dark`;
}

function hour12(h: number): string {
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour} ${suffix}`;
}
